/**
 * Dünner Client um die API. Die Typen kommen aus @stammbaum/shared,
 * damit Frontend und Backend nicht auseinanderlaufen können.
 */
import type { Genauigkeit, Ortsart, Namensart } from "@stammbaum/shared";

const basis = import.meta.env.VITE_API_URL ?? "";

/** Fehler mit HTTP-Status, damit der Aufrufer 401 von echten Fehlern trennen kann. */
export class ApiFehler extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiFehler";
  }
}

async function hole<T>(pfad: string, init?: RequestInit): Promise<T> {
  // Content-Type nur setzen, wenn es auch einen Rumpf gibt. Sonst weist
  // Fastify die Anfrage ab: "Body cannot be empty when content-type is set
  // to 'application/json'" — was jedes DELETE treffen würde.
  const kopfzeilen: Record<string, string> = init?.body
    ? { "Content-Type": "application/json" }
    : {};

  const antwort = await fetch(basis + pfad, {
    credentials: "include",
    ...init,
    headers: { ...kopfzeilen, ...(init?.headers as Record<string, string> | undefined) },
  });
  if (!antwort.ok) {
    const koerper = await antwort.json().catch(() => ({}) as { fehler?: string });
    throw new ApiFehler(koerper.fehler ?? `Fehler ${antwort.status}`, antwort.status);
  }
  return antwort.json() as Promise<T>;
}

export interface AngemeldeterBenutzer {
  id: number;
  anzeigename: string;
  rolle: "besitzer" | "bearbeiter" | "betrachter";
  personId: number | null;
  maxBlutsgrad?: number;
  schwiegerSichtbar?: boolean;
  /** Sieht dieser Zugang auch laengst Verstorbene ausserhalb der Verwandtschaft? */
  verstorbeneSichtbar?: boolean;
  /** Was er ändern darf — eigener, kleinerer Radius. */
  bearbeitenBlutsgrad?: number;
  bearbeitenVerstorbene?: boolean;
}

/** Eine Zeile der Benutzerverwaltung. Ohne Passworthash - den gibt die API nicht her. */
export interface BenutzerZeile {
  id: number;
  email: string;
  anzeigename: string;
  rolle: "besitzer" | "bearbeiter" | "betrachter";
  personId: number | null;
  maxBlutsgrad: number;
  schwiegerSichtbar: boolean;
  maxSchwiegergrad: number;
  verstorbeneSichtbar: boolean;
  /** Derselbe Radius fürs Bearbeiten, immer ≤ den Lesewerten. */
  bearbeitenBlutsgrad: number;
  bearbeitenSchwiegergrad: number;
  bearbeitenVerstorbene: boolean;
  aktiv: boolean;
  letzterLoginAm: string | null;
  erstelltAm: string;
  /** Hat die Person schon ein Passwort gesetzt? */
  hatPasswort: boolean;
  /** Liegt gerade ein gültiger Einladungslink vor? */
  offeneEinladung: boolean;
  person: { id: number; vorname: string; famname: string } | null;
}

/** Was beim Anlegen eines Zugangs mitgegeben wird. */
export interface BenutzerEingabe {
  email: string;
  anzeigename: string;
  rolle: "besitzer" | "bearbeiter" | "betrachter";
  personId?: number | null;
  maxBlutsgrad?: number;
  schwiegerSichtbar?: boolean;
  maxSchwiegergrad?: number;
  verstorbeneSichtbar?: boolean;
  bearbeitenBlutsgrad?: number;
  bearbeitenSchwiegergrad?: number;
  bearbeitenVerstorbene?: boolean;
}

/** Eine Zeile des Ortsregisters. */
export interface OrtRegisterZeile {
  id: number;
  name: string;
  teilVonId: number | null;
  art: Ortsart;
  plz: string | null;
  land: string | null;
  breitengrad: number | null;
  laengengrad: number | null;
  /** Ganzer Weg, unterste Ebene zuerst. */
  pfad: string;
  /** Wie oft der Ort an Personen, Ereignissen und Bildern hängt. */
  verwendungen: number;
  unterorte: number;
  doppelterName: boolean;
}

export interface PersonKurz {
  id: number;
  vorname: string;
  famname: string;
  /** Der im Alltag verwendete Vorname, wenn es nicht der erste ist. */
  rufname: string | null;
  maedchenname: string | null;
  geschlecht: string;
  /**
   * Beide Daten vollständig. Bei Lebenden hat der Server vorher gekürzt:
   * `geburtVon` steht dann auf dem 1. Jänner und `geburtGenauigkeit` auf
   * "jahr" — deshalb nie `von` allein anzeigen, sondern über `datumKurz`.
   */
  geburtVon: string | null;
  geburtBis: string | null;
  geburtGenauigkeit: Genauigkeit | null;
  geburtText: string | null;
  todVon: string | null;
  todBis: string | null;
  todGenauigkeit: Genauigkeit | null;
  todText: string | null;
  kurzinfo?: string | null;
  /**
   * Gilt die Person als lebend? Entscheidet in der Infokarte, ob aus
   * einem fehlenden Sterbedatum „heute" wird - sonst stuende der 1823
   * Geborene mit 203 Jahren da.
   */
  giltAlsLebend?: boolean;
  bezeichnung?: string;
  entfernung?: {
    blut: number;
    schwieger: number;
    aufwaerts: number;
    abwaerts: number;
    /** Blutsschritte vor der ersten Partnerkante — trennt Stief von Schwieger. */
    blutVorSchwieger?: number;
  };
  portrait?: { medienId: number; tagId: number | null } | null;
  /**
   * Bei einem Treffer über einen abweichenden Namen: welcher es war.
   * Sonst null — die Trefferzeile soll sagen, warum sie dasteht.
   */
  gefundenUeber?: string | null;
}

/** Eine Zeile der Medienliste. */
export interface MedienZeile {
  id: number;
  dateiname: string;
  titel: string | null;
  mimetyp: string;
  bytes: number;
  breite: number | null;
  hoehe: number | null;
  aufnahmeVon: string | null;
  aufnahmeBis: string | null;
  aufnahmeGenauigkeit: Genauigkeit | null;
  aufnahmeText: string | null;
  aufgenommenAm: string | null;
  /**
   * Woher das angezeigte Datum stammt: das gepflegte Aufnahmedatum oder
   * der EXIF-Zeitstempel. Ohne diese Angabe sieht ein Scandatum wie eine
   * Aufnahme aus.
   */
  datumQuelle: "aufnahme" | "exif" | null;
  erstelltAm: string;
  erfasser: { id: number; anzeigename: string } | null;
  ortName: string | null;
  ortPfad: string | null;
  /** Nur die sichtbaren markierten Personen, höchstens vier. */
  personen: { id: number; vorname: string; famname: string }[];
  anzahlPersonen: number;
}

/** Ein abweichender Name, wie ihn die Personenansicht liefert. */
export interface PersonennameZeile {
  id: number;
  vorname: string | null;
  famname: string | null;
  /** Bei gekürzten Datensätzen null — „angenommen" wäre eine Aussage. */
  art: Namensart | null;
  /** Fundstelle in Worten. Bei gekürzten Datensätzen null. */
  notiz: string | null;
}

/** Eine Familie, so knapp wie sie für eine Beschriftung gebraucht wird. */
export interface FamilieAnzeige {
  id: number;
  bezeichnung: string | null;
  mitglieder: { id: number; vorname: string; famname: string; rolle: string }[];
}

/** Ein Ereignis mit allem, was in eine Zeile gehört. */
export interface EreignisAnzeige {
  id: number;
  art: string;
  datumVon: string | null;
  datumBis: string | null;
  datumGenauigkeit: Genauigkeit | null;
  datumText: string | null;
  ortName: string | null;
  beteiligte: { id: number; vorname: string; famname: string; rolle: string }[];
}

export const api = {
  ich: () => hole<AngemeldeterBenutzer>("/api/ich"),

  anmelden: (email: string, passwort: string) =>
    hole<AngemeldeterBenutzer>("/api/anmelden", {
      method: "POST",
      body: JSON.stringify({ email, passwort }),
    }),

  abmelden: () => hole<{ ok: boolean }>("/api/abmelden", { method: "POST" }),

  // ---- Benutzerverwaltung (nur für den Besitzer) -------------------------

  benutzerListe: () => hole<{ benutzer: BenutzerZeile[] }>("/api/benutzer"),

  /**
   * Legt den Zugang an und gibt das Einladungstoken zurück — EINMALIG.
   * Gespeichert wird davon nur ein Hash, ein zweites Mal gibt es dasselbe
   * Token also nicht; wer den Link verliert, erzeugt einen neuen.
   */
  benutzerAnlegen: (werte: BenutzerEingabe) =>
    hole<{ benutzer: BenutzerZeile; token: string }>("/api/benutzer", {
      method: "POST",
      body: JSON.stringify(werte),
    }),

  /**
   * `passwort` nur beim Ändern der EIGENEN Anmelde-Adresse — der Server
   * verlangt dort die Bestätigung, siehe routes/benutzer.ts.
   */
  benutzerAendern: (
    id: number,
    werte: Partial<BenutzerEingabe> & { aktiv?: boolean; passwort?: string },
  ) =>
    hole<{ benutzer: BenutzerZeile }>(`/api/benutzer/${id}`, {
      method: "PATCH",
      body: JSON.stringify(werte),
    }),

  /** Neuer Einladungslink — dient auch als Passwort-Zurücksetzung. */
  einladungErzeugen: (id: number) =>
    hole<{ token: string; tage: number }>(`/api/benutzer/${id}/einladung`, { method: "POST" }),

  // ---- Einladung einlösen (ohne Anmeldung) ------------------------------

  einladungPruefen: (token: string) =>
    hole<{ email: string; anzeigename: string; erstmalig: boolean }>(
      `/api/einladung/${encodeURIComponent(token)}`,
    ),

  einladungEinloesen: (token: string, passwort: string) =>
    hole<{ ok: boolean; email: string }>(`/api/einladung/${encodeURIComponent(token)}`, {
      method: "POST",
      body: JSON.stringify({ passwort }),
    }),

  // ---- Der eigene Zugang ------------------------------------------------

  ichAendern: (anzeigename: string) =>
    hole<AngemeldeterBenutzer>("/api/ich", {
      method: "PATCH",
      body: JSON.stringify({ anzeigename }),
    }),

  passwortAendern: (alt: string, neu: string) =>
    hole<{ ok: boolean }>("/api/ich/passwort", {
      method: "POST",
      body: JSON.stringify({ alt, neu }),
    }),

  personen: (suche: string) =>
    hole<{ treffer: PersonKurz[] }>(`/api/personen?suche=${encodeURIComponent(suche)}`),

  person: (id: number) => hole<any>(`/api/personen/${id}`),

  personAendern: (id: number, werte: unknown) =>
    hole<any>(`/api/personen/${id}`, { method: "PATCH", body: JSON.stringify(werte) }),

  personAnlegen: (werte: unknown) =>
    hole<any>("/api/personen", { method: "POST", body: JSON.stringify(werte) }),

  /** Neue Person anlegen und gleich an eine bestehende hängen. */
  verwandtenAnlegen: (eingabe: {
    ausgehendVon: number;
    art: "elternteil" | "geschwister" | "partner" | "kind";
    familieId: number | null;
    person: unknown;
  }) =>
    hole<{ person: { id: number }; familieId: number }>("/api/verwandte", {
      method: "POST",
      body: JSON.stringify(eingabe),
    }),

  /** Eine Person ganz entfernen — für Dubletten und Fehlgriffe. */
  personLoeschen: (id: number) =>
    hole<{ ok: boolean; ereignisse: number; familien: number }>(`/api/personen/${id}`, {
      method: "DELETE",
    }),

  /** Familie umbenennen. */
  familieAendern: (id: number, werte: { bezeichnung: string | null }) =>
    hole<{ id: number; bezeichnung: string | null }>(`/api/familien/${id}`, {
      method: "PATCH",
      body: JSON.stringify(werte),
    }),

  /** Eine weitere (leere) Familie für eine Person — für die zweite Ehe. */
  familieAnlegen: (eingabe: { personId: number; rolle: "elternteil" | "kind" }) =>
    hole<{ id: number }>("/api/familien", { method: "POST", body: JSON.stringify(eingabe) }),

  /** Eine bereits vorhandene Person zusätzlich in eine Familie hängen. */
  mitgliedHinzufuegen: (
    familieId: number,
    eingabe: { personId: number; rolle: "elternteil" | "kind" },
  ) =>
    hole<{ ok: boolean }>(`/api/familien/${familieId}/mitglieder`, {
      method: "POST",
      body: JSON.stringify({ ...eingabe, verbindungsart: "leiblich", reihung: 0 }),
    }),

  /** Zugehörigkeit lösen. Die Person selbst bleibt bestehen. */
  mitgliedLoesen: (familieId: number, personId: number) =>
    hole<{ ok: boolean }>(`/api/familien/${familieId}/mitglieder/${personId}`, {
      method: "DELETE",
    }),

  // ---- Ereignisse ----------------------------------------------------------

  ereignis: (id: number) => hole<any>(`/api/ereignisse/${id}`),

  ereignisAnlegen: (werte: unknown) =>
    hole<{ id: number }>("/api/ereignisse", { method: "POST", body: JSON.stringify(werte) }),

  ereignisAendern: (id: number, werte: unknown) =>
    hole<{ id: number }>(`/api/ereignisse/${id}`, { method: "PATCH", body: JSON.stringify(werte) }),

  ereignisLoeschen: (id: number) =>
    hole<{ ok: boolean }>(`/api/ereignisse/${id}`, { method: "DELETE" }),

  /** Ortsvorschläge für die Ereignismaske. */
  /**
   * Ortsvorschläge. `pfad` ist der ganze Weg („Haimschlag 30, Echsenbach,
   * Niederösterreich") und zugleich das, was in das Eingabefeld gehört —
   * der bloße Name wäre nicht eindeutig.
   */
  orte: (suche: string) =>
    hole<{
      orte: {
        id: number;
        name: string;
        pfad: string;
        plz: string | null;
        /** Wie oft der Ort schon benutzt wird - die API sortiert danach. */
        verwendungen: number;
      }[];
      /** Wie viele Orte auf den Suchtext passen — auch die nicht gelieferten. */
      treffer: number;
      /** true heißt: es gibt mehr, als hier stehen. Weitertippen hilft. */
      abgeschnitten: boolean;
    }>(`/api/orte?suche=${encodeURIComponent(suche)}`),

  // ---- Karte ---------------------------------------------------------------

  /**
   * Alle Orte mit Koordinaten. Die Zahlen sind das, was DIESER Benutzer
   * sehen darf — eine Blase „12", die aufgeklappt zwei Namen zeigt, wäre
   * schlimmer als eine Blase „2".
   */
  karte: () =>
    hole<{
      punkte: {
        id: number;
        name: string;
        pfad: string;
        art: Ortsart;
        breitengrad: number;
        laengengrad: number;
        personen: number;
        ereignisse: number;
      }[];
      /** Orte mit Verwendungen, aber ohne Koordinaten. */
      ohneKoordinaten: number;
    }>("/api/karte"),

  /** Wer und was an einem Ort hängt. */
  karteOrt: (id: number) =>
    hole<{
      ort: {
        id: number;
        name: string;
        pfad: string;
        art: Ortsart;
        plz: string | null;
        breitengrad: number | null;
        laengengrad: number | null;
      };
      personen: (PersonKurz & { geburtsort: boolean; wohnort: boolean })[];
      ereignisse: {
        id: number;
        art: string;
        datumVon: string | null;
        datumBis: string | null;
        datumGenauigkeit: Genauigkeit | null;
        datumText: string | null;
        titel: string | null;
        beteiligte: { id: number; vorname: string; famname: string }[];
      }[];
    }>(`/api/karte/${id}`),

  /** Das ganze Ortsregister - Pfad, Zähler, Dublettenmarkierung. */
  ortsregister: () =>
    hole<{ orte: OrtRegisterZeile[] }>("/api/orte/register"),

  ortAendern: (
    id: number,
    werte: {
      name?: string;
      art?: Ortsart;
      plz?: string | null;
      land?: string | null;
      breitengrad?: number | null;
      laengengrad?: number | null;
      teilVonId?: number | null;
    },
  ) => hole<{ ok: boolean }>(`/api/orte/${id}`, { method: "PATCH", body: JSON.stringify(werte) }),

  /** Ort `id` in `nachId` überführen, samt allem, was daran hängt. */
  orteZusammenfuehren: (id: number, nachId: number) =>
    hole<{ ok: boolean; orte: number; umgehaengt: number; umbenannt: string | null }>(
      `/api/orte/${id}/zusammenfuehren`,
      { method: "POST", body: JSON.stringify({ nachId }) },
    ),

  /** Nur möglich, solange nichts mehr daran hängt. */
  ortLoeschen: (id: number) => hole<{ ok: boolean }>(`/api/orte/${id}`, { method: "DELETE" }),

  /**
   * Alle Orte löschen, an denen weder Person noch Ereignis noch Bild noch
   * Unterort hängt — in einem Durchgang. `danachVerwaist` sagt, wie viele
   * dadurch neu verwaist sind; ein zweiter Aufruf räumt die weg.
   */
  verwaisteOrteLoeschen: () =>
    hole<{ ok: boolean; geloescht: number; danachVerwaist: number }>(
      "/api/orte/verwaiste-loeschen",
      { method: "POST" },
    ),

  // ---- Bilder --------------------------------------------------------------

  medium: (id: number) =>
    hole<{
      medium: {
        id: number;
        dateiname: string;
        mimetyp: string;
        /** Groesse der Originaldatei - steht bei „Original ansehen" dabei. */
        bytes: number;
        breite: number;
        hoehe: number;
        titel: string | null;
        beschreibung: string | null;
        /** Zeitstempel aus EXIF - das unveränderte Zeugnis. */
        aufgenommenAm: string | null;
        /** Das gepflegte, unscharfe Aufnahmedatum. */
        aufnahmeVon: string | null;
        aufnahmeBis: string | null;
        aufnahmeGenauigkeit: Genauigkeit | null;
        aufnahmeText: string | null;
        ortName: string | null;
        exif: Record<string, unknown> | null;
      };
      tags: {
        tag: { id: number; x: number; y: number; breite: number; hoehe: number; personId: number | null; beschriftung: string | null };
        person: { id: number; vorname: string; famname: string } | null;
      }[];
      verknuepfungen: { zielTyp: string; zielId: number }[];
      zugeordnetePersonen: { id: number; vorname: string; famname: string; geburtVon: string | null }[];
      zugeordneteFamilien: FamilieAnzeige[];
      zugeordneteEreignisse: EreignisAnzeige[];
      portraits: { personId: number; medienId: number; tagId: number | null }[];
    }>(`/api/medien/${id}`),

  /** Angaben zum Bild ändern: Aufnahmedatum, Ort, Titel, Beschreibung. */
  medienAendern: (id: number, werte: unknown) =>
    hole<{ id: number }>(`/api/medien/${id}`, { method: "PATCH", body: JSON.stringify(werte) }),

  /**
   * Die Medienliste. `gesamt` ist die Zahl der sichtbaren Treffer, nicht
   * die der gelieferten Zeilen — daran hängt das „mehr laden".
   */
  medienListe: (p: {
    suche?: string;
    sortierung?: "aufnahme" | "erfasser" | "ort" | "hochgeladen" | "groesse" | "name";
    richtung?: "auf" | "ab";
    limit?: number;
    offset?: number;
  }) => {
    const f = new URLSearchParams();
    if (p.suche) f.set("suche", p.suche);
    if (p.sortierung) f.set("sortierung", p.sortierung);
    if (p.richtung) f.set("richtung", p.richtung);
    if (p.limit) f.set("limit", String(p.limit));
    if (p.offset) f.set("offset", String(p.offset));
    return hole<{ gesamt: number; medien: MedienZeile[] }>(`/api/medien?${f}`);
  },

  /** Wohin dieses Bild sonst noch passen könnte — aus den Leuten darauf. */
  medienZiele: (id: number) =>
    hole<{ familien: FamilieAnzeige[]; ereignisse: EreignisAnzeige[] }>(
      `/api/medien/${id}/ziele`,
    ),

  /** Bild hochladen. Kein Content-Type setzen — der Browser setzt die Grenze. */
  fotoHochladen: async (datei: File) => {
    const daten = new FormData();
    daten.append("datei", datei);
    const antwort = await fetch(basis + "/api/medien", {
      method: "POST",
      credentials: "include",
      body: daten,
    });
    if (!antwort.ok) {
      const koerper = await antwort.json().catch(() => ({}) as { fehler?: string });
      throw new ApiFehler(koerper.fehler ?? `Fehler ${antwort.status}`, antwort.status);
    }
    return (await antwort.json()) as { medium: { id: number }; neu: boolean };
  },

  tagAnlegen: (
    medienId: number,
    tag: { x: number; y: number; breite: number; hoehe: number; personId?: number | null; beschriftung?: string | null },
  ) =>
    hole<{ id: number }>(`/api/medien/${medienId}/tags`, {
      method: "POST",
      body: JSON.stringify(tag),
    }),

  tagLoeschen: (medienId: number, tagId: number) =>
    hole<{ ok: boolean }>(`/api/medien/${medienId}/tags/${tagId}`, { method: "DELETE" }),

  portraitSetzen: (personId: number, medienId: number | null, tagId?: number | null) =>
    hole<{ ok: boolean }>(`/api/personen/${personId}/portrait`, {
      method: "PUT",
      body: JSON.stringify({ medienId, tagId: tagId ?? null }),
    }),

  /** Zuordnung lösen. Das Bild bleibt, es hängt ja womöglich an anderen. */
  zuordnungLoesen: (medienId: number, zielTyp: "person" | "familie" | "ereignis", zielId: number) =>
    hole<{ ok: boolean; entfernteTags: number; entferntesPortrait: boolean }>(
      `/api/medien/${medienId}/verknuepfen/${zielTyp}/${zielId}`,
      { method: "DELETE" },
    ),

  /** Bild ganz entfernen. Nur für den Besitzer. */
  medienLoeschen: (medienId: number) =>
    hole<{ ok: boolean }>(`/api/medien/${medienId}`, { method: "DELETE" }),

  medienVerknuepfen: (medienId: number, zielTyp: "person" | "familie" | "ereignis", zielId: number) =>
    hole<{ ok: boolean }>(`/api/medien/${medienId}/verknuepfen`, {
      method: "POST",
      body: JSON.stringify({ zielTyp, zielId }),
    }),

  stammbaum: (id: number, grad = 3) =>
    hole<{
      wurzel: number;
      personen: PersonKurz[];
      familien: { id: number; bezeichnung: string | null }[];
      kanten: { familieId: number; personId: number; rolle: "elternteil" | "kind" }[];
    }>(`/api/stammbaum/${id}?grad=${grad}`),
};

/**
 * Die Originaldatei — für Urkunden der eigentliche Inhalt, denn die Vorschau
 * zeigt nur die erste Seite als Bild.
 */
export function dateiUrl(medienId: number, herunterladen = false) {
  return `${basis}/medien/${medienId}/datei${herunterladen ? "?herunterladen=1" : ""}`;
}

/** Ein PDF? Dann gibt es nichts zu markieren, und das Original zählt. */
export const istPdf = (mimetyp?: string | null) => mimetyp === "application/pdf";

/** Bild-URL. Ausschnitt (Portrait aus einem Gruppenfoto) via tagId. */
export function bildUrl(medienId: number, groesse: 64 | 160 | 480 | 1200, tagId?: number | null) {
  const t = tagId ? `?tag=${tagId}` : "";
  return `${basis}/medien/${medienId}/${groesse}.webp${t}`;
}
